import angular from 'angular';
import { find, map, forEach, concat } from 'lodash';

import { getErrorMessage, updateErrorMessage } from 'application/util/error.util';
import { getMaxNumberOfCollaborators } from 'common/util/plan.util';

import './team-edit-modal.style.sass';
import template from './team-edit-modal.tpl.pug';

class TeamEditModalController {
    /* @ngInject */
    constructor($q, sessionService, modalService, teamService, collaboratorService, notificationService) {
        this.$q = $q;
        this.sessionService = sessionService;
        this.modalService = modalService;
        this.teamService = teamService;
        this.collaboratorService = collaboratorService;
        this.notificationService = notificationService;
    }

    $onInit() {
        this.existing = this.team && this.team.id;
        this.canDelete = this.existing && this.canDelete;

        this.load();
    }

    load() {
        this.loading = true;

        return this.$q.all([
            this.sessionService.initialize(),
            this.collaboratorService.getAll()
        ]).then(([session, collaborators]) => {
            this.plan = session.plan;

            let user = session.user;
            this.currentUser = user;
            let maxMemberCount = getMaxNumberOfCollaborators(this.plan);
            let isOwner = !this.team || !this.team.owner || this.team.owner.id === user.id;

            this.canManageCollaboratorsIfUpgrade = isOwner && maxMemberCount === 0;

            if (this.existing) {
                return this.loadMembers(user);
            }

            return this.loadCollaborators(collaborators, user);
        }).catch((result) => {
            this.notificationService.showError(getErrorMessage(result, `We were not able to retrieve your account collaborators.`));
        }).finally(() => {
            this.loading = false;
        });
    }

    isCurrentUser(member) {
        if (!member || !this.currentUser) {
            return false;
        }

        if (this.currentUser.id && member.id) {
            return this.currentUser.id === member.id;
        }

        if (this.currentUser.email && member.email) {
            return this.currentUser.email === member.email;
        }

        return false;
    }

    loadMembers(user) {
        return this.teamService.getMembers(this.team.id).then((members) => {
            this.updateMembers(map(members, (member) => angular.merge({}, member, {
                canEdit: user.email !== member.email && !member.owner
            })));
        }).catch((result) => {
            this.updateMembers([]);
            this.notificationService.showError(getErrorMessage(result, `We were not able to retrieve the team members.`));
        });
    }

    loadCollaborators(collaborators, user) {
        collaborators = map(collaborators, (collaborator) => angular.merge({}, collaborator, {
            shared: false,
            owner: false,
            canEdit: true
        }));

        let members = concat([angular.merge({}, user, {
            shared: true,
            owner: true,
            canEdit: false
        })], collaborators);

        this.updateMembers(members);
    }

    toggleShare(member) {
        if (member.canEdit) {
            member.shared = !member.shared;
        }
    }

    updateMembers(members) {
        this.members = members;
        this.initialMembers = map(members, (member) => angular.merge({}, member));
    }

    manageCategories() {
        return this.modalService.open({
            component: 'dbACategoryManageModal',
            bindings: {
                teamId: this.team.id
            }
        }).catch(() => null);
    }

    save() {
        let promise = this.existing
            ? this.teamService.update(this.team)
            : this.teamService.create(this.team);

        return promise.then((team) => {
            let addedMembers = this.getAddedMembers();
            let removedMembers = this.getRemovedMembers();

            if (addedMembers.length > 0 || removedMembers.length > 0) {
                return this.teamService.updateMembers(team.id, addedMembers, removedMembers)
                    .then(this.close.bind(this, team))
                    .catch((result) => this.$q.reject(updateErrorMessage(result, `We were not able to update the collaborators.`)));
            }

            return this.close(team);
        }).catch((result) => {
            this.notificationService.showError(getErrorMessage(result, `We were not able to save the team.`));
        });
    }

    remove() {
        return this.modalService.open({
            component: 'dbAConfirmModal',
            bindings: {
                title: 'Delete team',
                message: [
                    `Deleting a team cannot be undone. All the calendars and categories associated with this team will be erased.`,
                    `Are you still sure about it?`
                ],
                onClose: () => {
                    return this.teamService.remove(this.team)
                        .then(this.close.bind(this, this.team))
                        .catch((result) => {
                            this.notificationService.showError(getErrorMessage(result, `We were not able to delete the team.`));
                            return this.$q.reject(result);
                        });
                }
            }
        }).catch(() => null);
    }

    leave() {
        return this.modalService.open({
            component: 'dbAConfirmModal',
            bindings: {
                title: 'Leave team',
                message: [
                    `You are about to leave this team. If you change your mind later on, you have to contact the team owner (${this.team.owner.name}) to add you back to this team.`,
                    `By leaving the team you lose access to all the calendars and categories associated with this team.`,
                    `Are you still sure about it?`
                ],
                primaryButton: [
                    `Leave`,
                    `Leaving...`
                ],
                onClose: () => {
                    return this.teamService.leave(this.team)
                        .then(this.close.bind(this, this.team))
                        .catch((result) => {
                            this.notificationService.showError(getErrorMessage(result, `We were not able to remove you from the team.`));
                            return this.$q.reject(result);
                        });
                }
            }
        }).catch(() => null);
    }

    manageCollaborators() {
        return this.modalService.open({
            component: 'dbACollaboratorManageModal'
        }).then((emails) => {
            return this.load().then(() => {
                forEach(this.members, (member) => {
                    member.shared = find(emails, (email) => email === member.email) || member.shared;
                });
            });
        });
    }

    getAddedMembers() {
        return this.members.filter((member) => {
            let initial = find(this.initialMembers, (initial) => this.userComparator(member, initial));
            return !initial || initial.shared !== member.shared && member.shared;
        });
    }

    getRemovedMembers() {
        return this.initialMembers.filter((initial) => {
            let member = find(this.members, (member) => this.userComparator(member, initial));
            return member && member.shared !== initial.shared && !member.shared;
        });
    }

    userComparator(user1, user2) {
        return user1.email === user2.email;
    }

    close(team) {
        return this.onClose({
            data: team
        });
    }

    dismiss() {
        return this.onDismiss();
    }
}

export default {
    selector: 'dbATeamEditModal',
    template: template(),
    controller: TeamEditModalController,
    bindings: {
        team: '<',

        canEdit: '<',
        canDelete: '<',
        canManageCollaborators: '<',
        canManageCategories: '<',

        onDismiss: '&',
        onClose: '&'
    }
};



// WEBPACK FOOTER //
// ./src/application/component/modals/component/team-edit-modal/team-edit-modal.cmpt.js